/**
 * Onboarding Service
 * Handles API calls for user onboarding flow (SKU selection)
 */

import { apiClient } from './api.client';

/**
 * Available SKU for selection (subset of full SKU data)
 */
export interface AvailableSKU {
  id: string;
  name: string;
  description?: string;
  pricing: {
    monthly: number;
    yearly?: number;
    currency: string;
  };
  limits: {
    maxBrands: number;
    maxDeals: number;
    maxDeliverablesPerDeal: number;
    maxFileSize: number;
    maxTotalStorage: number;
    maxFilesPerNote: number;
    allowedFileTypes: {
      documents: boolean;
      images: boolean;
      videos: boolean;
    };
    features: {
      aiContentGeneration: boolean;
      analytics: boolean;
      customBranding: boolean;
      apiAccess: boolean;
      prioritySupport: boolean;
    };
  };
  features?: string[];
  displayOrder: number;
}

/**
 * Available SKUs response
 */
export interface AvailableSKUsResponse {
  success: boolean;
  data: AvailableSKU[];
  timestamp: string;
}

/**
 * Select SKU response
 */
export interface SelectSKUResponse {
  success: boolean;
  message: string;
  data: {
    skuId: string;
    skuName: string;
  };
  timestamp: string;
}

/**
 * Onboarding Service Class
 */
class OnboardingService {
  /**
   * Get available SKUs for selection during onboarding
   * @returns List of public, active SKUs
   */
  async getAvailableSKUs(): Promise<AvailableSKU[]> {
    const response = await apiClient.get<AvailableSKUsResponse>('/api/users/available-skus');

    if (response.data.success && Array.isArray(response.data.data)) {
      return response.data.data;
    }

    throw new Error('Failed to fetch available plans');
  }

  /**
   * Select a SKU during onboarding
   * @param skuId - SKU ID to select
   * @returns Selection result
   */
  async selectSKU(skuId: string): Promise<SelectSKUResponse['data']> {
    const response = await apiClient.post<SelectSKUResponse>('/api/users/select-sku', { skuId });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to select plan');
  }
}

/**
 * Export singleton instance
 */
export const onboardingService = new OnboardingService();

export default onboardingService;
